import type { MockRecord } from '../database.ts';
import { isTableName } from './protocol.ts';

export type SyncPortal = 'internal' | 'vendor';
type Direction = 'both' | 'to-vendor' | 'to-internal';

/**
 * What legacy QCDOC shares between the internal and vendor applications. The vendor application reads and
 * writes the same business database as the internal one, but only for VQCA signing, COA submission,
 * packaging and its own identity; everything else (samples, QA lab, formulas, COQ, shipments, alerts,
 * access control, reports) is internal and is never copied. Tables that are not listed are not shared.
 */
const TABLES: Record<string, Direction> = {
  // Agreements: the internal portal issues and signs them, the vendor portal countersigns.
  vqcas: 'both', signLogs: 'both',
  // Issued with the agreement and never edited by suppliers.
  vqcaSpecs: 'to-vendor',
  // COA: submitted by suppliers, reviewed internally.
  coas: 'both', coaPos: 'both', coaExperiments: 'both', coaResults: 'both', attachments: 'both',
  // Packaging: supplier-created packages, internally managed packager assignments.
  packages: 'both', packageLots: 'both', packagePos: 'both',
  packagerVendors: 'to-vendor', packagerProducts: 'to-vendor',
  // Supplier identity: registration and approval happen on opposite sides.
  users: 'both', vendors: 'both', vendorsInRoles: 'both', vendorUsersInRoles: 'both', userRequests: 'both',
  // Product master and reference data are authored internally.
  products: 'to-vendor', specs: 'to-vendor', stps: 'to-vendor', categories: 'to-vendor', labs: 'to-vendor', psls: 'to-vendor',
  productTypes: 'to-vendor', regTypes: 'to-vendor', specTypes: 'to-vendor', countries: 'to-vendor', states: 'to-vendor',
  securityQuestions: 'to-vendor',
  // Legacy audit and integrity history of supplier actions is kept internally.
  audit: 'to-internal', integrityEvents: 'to-internal',
};

export function isSyncedTable(table: string): boolean {
  return isTableName(table) && Object.hasOwn(TABLES, table);
}

/** Whether the portal is the authoring side of the table, so its changes are sent. */
export function mayOriginate(table: string, portal: SyncPortal): boolean {
  const direction = TABLES[table];
  return direction === 'both' || direction === (portal === 'internal' ? 'to-vendor' : 'to-internal');
}

/** Whether the portal accepts changes to the table from its peer. */
export function mayReceive(table: string, portal: SyncPortal): boolean {
  const direction = TABLES[table];
  return direction === 'both' || direction === (portal === 'internal' ? 'to-internal' : 'to-vendor');
}

/** Record-level scope of shared tables: internal accounts, requests and sample attachments stay internal. */
export function isSyncedRecord(table: string, record: MockRecord): boolean {
  if (table === 'users' || table === 'userRequests') return record.portal === 'QCDOC-VENDOR';
  if (table === 'attachments') return Boolean(record.coaId);
  return true;
}
